import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  User,
  Phone,
  MapPin,
  Calendar,
  FileText,
  Heart,
  Loader2,
  Mail,
  Globe,
  Info,
  Shield,
  MessageCircle,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { AddressAutocomplete } from '../maps/AddressAutocomplete';
import { CustomFieldInlineEditor } from '../ui/CustomFieldInlineEditor';

// Country codes for phone
const countryCodes = [
  { code: '+57', country: 'Colombia', flag: '🇨🇴' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+52', country: 'México', flag: '🇲🇽' },
  { code: '+51', country: 'Perú', flag: '🇵🇪' },
  { code: '+54', country: 'Argentina', flag: '🇦🇷' },
  { code: '+56', country: 'Chile', flag: '🇨🇱' },
  { code: '+593', country: 'Ecuador', flag: '🇪🇨' },
  { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
  { code: '+507', country: 'Panamá', flag: '🇵🇦' },
  { code: '+34', country: 'España', flag: '🇪🇸' },
];

export const PatientFormPanel = ({ 
  patient, 
  isOpen, 
  onClose, 
  onSubmit,
  countries = [],
  cities = []
}) => {
  const { t } = useTranslation('patients');
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    document_type: 'CC',
    document_number: '',
    birth_date: '',
    gender: 'male',
    phone_country_code: '+57',
    phone: '',
    mobile: '',
    email: '',
    whatsapp_username: '',
    address: {
      street: '',
      city: '',
      city_id: '',
      department: '',
      country: '',
      country_id: ''
    },
    blood_type: '',
    notes: '',
    custom_field_values: []
  });

  // Reset form when patient changes
  useEffect(() => {
    if (isOpen) {
      if (patient) {
        setFormData({
          ...patient,
          address: patient.address || {
            street: '',
            city: '',
            city_id: '',
            department: '',
            country: '',
            country_id: ''
          }
        });
      } else {
        setFormData({
          first_name: '',
          last_name: '',
          document_type: 'CC',
          document_number: '',
          birth_date: '',
          gender: 'male',
          phone_country_code: '+57',
          phone: '',
          mobile: '',
          email: '',
          whatsapp_username: '',
          address: {
            street: '',
            city: '',
            city_id: '',
            department: '',
            country: '',
            country_id: ''
          },
          blood_type: '',
          notes: '',
          custom_field_values: []
        });
      }
      setActiveTab('basic');
    }
  }, [patient, isOpen]);

  // Filter cities by selected country
  const filteredCities = useMemo(() => {
    if (formData.address?.country_id) {
      return cities.filter(c => c.country_id === formData.address.country_id && c.status === 'active');
    }
    return cities.filter(c => c.status === 'active');
  }, [formData.address?.country_id, cities]);

  const handleCountryChange = (countryId) => {
    const country = countries.find(c => c.id === countryId);
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        country_id: countryId,
        country: country?.name || '',
        city_id: '',
        city: '',
        department: ''
      }
    });
  };

  const handleCityChange = (cityId) => {
    const city = cities.find(c => c.id === cityId);
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        city_id: cityId,
        city: city?.name || '',
        department: city?.state_department || ''
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.first_name || !formData.last_name || !formData.document_number) {
      toast.error(t('form.requiredFields'));
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error saving patient:', error);
      toast.error(t('messages.errorCreating'));
    }
    setLoading(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-hidden flex flex-col p-0">
        {/* Header */}
        <SheetHeader className="px-6 py-4 border-b bg-gradient-to-r from-[#E63946]/5 to-transparent">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[#E63946]/10 flex items-center justify-center">
              <User className="h-6 w-6 text-[#E63946]" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold">
                {patient?.id ? t('form.editTitle') : t('form.title')}
              </SheetTitle>
              <SheetDescription>
                {patient?.id ? t('form.editSubtitle') : t('form.subtitle')}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="px-6 py-2 border-b justify-start rounded-none bg-transparent">
            <TabsTrigger value="basic" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Info className="h-4 w-4 mr-1" />
              {t('form.tabs.basic')}
            </TabsTrigger>
            <TabsTrigger value="contact" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Phone className="h-4 w-4 mr-1" />
              {t('form.tabs.contact')}
            </TabsTrigger>
            <TabsTrigger value="address" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <MapPin className="h-4 w-4 mr-1" />
              {t('form.tabs.address')}
            </TabsTrigger>
            <TabsTrigger value="medical" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Heart className="h-4 w-4 mr-1" />
              {t('form.tabs.medical')}
            </TabsTrigger>
            <TabsTrigger value="custom" className="data-[state=active]:bg-[#E63946]/10 data-[state=active]:text-[#E63946]">
              <Plus className="h-4 w-4 mr-1" />
              {t('form.tabs.custom')}
              {formData.custom_field_values?.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs px-1.5 py-0">
                  {formData.custom_field_values.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1">
            {/* Basic Info Tab */}
            <TabsContent value="basic" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.firstName')} *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    placeholder={t('form.firstNamePlaceholder')}
                    data-testid="patient-first-name"
                  />
                </div>
                <div>
                  <Label>{t('form.lastName')} *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    placeholder={t('form.lastNamePlaceholder')}
                    data-testid="patient-last-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.documentType')} *</Label>
                  <Select
                    value={formData.document_type}
                    onValueChange={(v) => setFormData({...formData, document_type: v})}
                  >
                    <SelectTrigger data-testid="patient-doc-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CC">{t('documentTypes.CC')}</SelectItem>
                      <SelectItem value="TI">{t('documentTypes.TI')}</SelectItem>
                      <SelectItem value="CE">{t('documentTypes.CE')}</SelectItem>
                      <SelectItem value="PA">{t('documentTypes.PA')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('form.documentNumber')} *</Label>
                  <Input
                    value={formData.document_number}
                    onChange={(e) => setFormData({...formData, document_number: e.target.value})}
                    placeholder={t('form.documentNumberPlaceholder')}
                    data-testid="patient-doc-number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.birthDate')}</Label>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({...formData, birth_date: e.target.value})}
                    data-testid="patient-birth-date"
                  />
                </div>
                <div>
                  <Label>{t('form.gender')}</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(v) => setFormData({...formData, gender: v})}
                  >
                    <SelectTrigger data-testid="patient-gender">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">{t('genders.male')}</SelectItem>
                      <SelectItem value="female">{t('genders.female')}</SelectItem>
                      <SelectItem value="other">{t('genders.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>

            {/* Contact Tab */}
            <TabsContent value="contact" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>{t('form.phoneCountryCode')}</Label>
                  <Select
                    value={formData.phone_country_code || '+57'}
                    onValueChange={(v) => setFormData({...formData, phone_country_code: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {countryCodes.map(cc => (
                        <SelectItem key={cc.code} value={cc.code}>
                          {cc.flag} {cc.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>{t('form.phone')}</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder={t('form.phonePlaceholder')}
                    data-testid="patient-phone"
                  />
                </div>
              </div>

              <div>
                <Label>{t('form.mobile')}</Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                  placeholder={t('form.mobilePlaceholder')}
                />
              </div>

              <div>
                <Label>{t('form.email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('form.emailPlaceholder')}
                    className="pl-9"
                  />
                </div>
              </div>

              <div>
                <Label>{t('form.whatsapp')}</Label>
                <div className="relative">
                  <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={formData.whatsapp_username}
                    onChange={(e) => setFormData({...formData, whatsapp_username: e.target.value})}
                    placeholder={t('form.whatsappPlaceholder')}
                    className="pl-9"
                  />
                </div>
              </div>
            </TabsContent>

            {/* Address Tab */}
            <TabsContent value="address" className="p-6 space-y-4 mt-0">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('form.country')}</Label>
                  <Select
                    value={formData.address?.country_id || ''}
                    onValueChange={handleCountryChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('form.selectCountry')} />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.filter(c => c.status === 'active').map(country => (
                        <SelectItem key={country.id} value={country.id}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('form.city')}</Label>
                  <Select
                    value={formData.address?.city_id || ''}
                    onValueChange={handleCityChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('form.selectCity')} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCities.map(city => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t('form.department')}</Label>
                <Input
                  value={formData.address?.department || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    address: {...formData.address, department: e.target.value}
                  })}
                  placeholder={t('form.departmentPlaceholder')}
                />
              </div>

              <div>
                <Label>{t('form.street')}</Label>
                <AddressAutocomplete
                  value={formData.address?.street || ''}
                  onChange={(address) => setFormData({
                    ...formData,
                    address: {...formData.address, street: address}
                  })}
                  onSelect={(place) => {
                    if (place) {
                      setFormData({
                        ...formData,
                        address: {
                          ...formData.address,
                          street: place.formatted_address || place.address
                        }
                      });
                    }
                  }}
                  placeholder={t('form.streetPlaceholder')}
                />
              </div>
            </TabsContent>

            {/* Medical Tab */}
            <TabsContent value="medical" className="p-6 space-y-4 mt-0">
              <div>
                <Label>{t('form.bloodType')}</Label>
                <Select
                  value={formData.blood_type || ''}
                  onValueChange={(v) => setFormData({...formData, blood_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('form.selectBloodType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A+">A+</SelectItem>
                    <SelectItem value="A-">A-</SelectItem>
                    <SelectItem value="B+">B+</SelectItem>
                    <SelectItem value="B-">B-</SelectItem>
                    <SelectItem value="AB+">AB+</SelectItem>
                    <SelectItem value="AB-">AB-</SelectItem>
                    <SelectItem value="O+">O+</SelectItem>
                    <SelectItem value="O-">O-</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('form.notes')}</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder={t('form.notesPlaceholder')}
                  rows={4}
                />
              </div>
            </TabsContent>

            {/* Custom Fields Tab */}
            <TabsContent value="custom" className="p-6 mt-0">
              <CustomFieldInlineEditor
                entityType="patient"
                customFieldValues={formData.custom_field_values || []}
                onValuesChange={(values) => setFormData({ ...formData, custom_field_values: values })}
              />
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-muted/30 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>{t('common:cancel')}</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-[#E63946] hover:bg-[#d32836]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {patient?.id ? t('form.update') : t('form.save')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PatientFormPanel;
